import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {listenerMiddleware} from "../../redux/listenerMiddleware";
import {authenticationSlice} from "../authentication/authenticationSlice";
import {convertToDateTime} from "../common/dateUtils/convertToDateTime";
import {ClienteLight} from "../anagrafiche/clienti/clientiSlice";

export interface OrdinamentoLista {
	asc: boolean;
	campo: string;
}

export interface PreferenzeLista {
	numero_record?: number;
	page?: number;
	ordinamento?: OrdinamentoLista;
    search?: string;
}

export interface PreferenzeUtente {
	drawer_aperto: boolean;
	dark_mode: boolean;
}

export interface UserStored {
    username?: string;
    // password?: string;
    rememberUsername?: boolean;
    // dataScadenzaPassword?: string;
    email?: string;
    access?: string;
    refresh?: string;
    refreshExpiresAt?: string;
    timeRefresh?: string;
}

export interface FiltriOdPLegacy extends PreferenzeLista{
    esercizio?:number[],
    codice_articolo?:string[],
    stati?:number[],
    data_da?:string,
    data_a?:string,
    cliente?:ClienteLight | null,
    reparto?:number[],
    operatori?:string[],
    id_documento?:number[],
    numero_registrazione?:string[],
    non_perfettamente_schedulati?:boolean,
}

export interface FiltriFrazioniOdP extends PreferenzeLista {
    odp?: number;
    stati?: number[];
    reparto?: number[];
    data_da?: string;
    data_a?: string;
}

export interface FiltriVisualizzazioneGantt extends PreferenzeLista {
    dataDa?: string;
    numeroSettimane: 2 | 4 | 6;
}

export interface FiltriOstacoli extends PreferenzeLista {
    tipi?: string[];
    risolto?: boolean;
    data_da?: string;
    data_a?: string;
    odpId?: number;
}

export interface FiltriFasiFrazioniReparto extends PreferenzeLista {
    reparto_id?: number;
    solo_ostacoli?: boolean;
    data_da?: string;
    data_a?: string;
}

export interface PreferenzeUtenteState {
	userStored?: UserStored;
	preferenzeUtente: PreferenzeUtente;
    filtriOdPSeguiti?: FiltriOdPLegacy;
    filtriOdPSpallaSchedulazione?: FiltriOdPLegacy;
    filtriFrazioniOdP?: FiltriFrazioniOdP;
    filtriVisualizzazioneGantt?: FiltriVisualizzazioneGantt;
    filtriOstacoli?: FiltriOstacoli;
    filtriFasiFrazioniReparto?: FiltriFasiFrazioniReparto;
}

const initialState: PreferenzeUtenteState = {
    userStored: undefined,
    preferenzeUtente: {drawer_aperto: true, dark_mode: false},
    filtriOdPSeguiti: undefined,
    filtriOdPSpallaSchedulazione: undefined,
    filtriFrazioniOdP: undefined,
    filtriOstacoli: undefined,
    filtriFasiFrazioniReparto: undefined,
    filtriVisualizzazioneGantt: {dataDa: convertToDateTime(new Date())?.setLocale("it").toFormat("yyyy-MM-dd"), numeroSettimane: 4},
    // preferenzeListaTasks: {numero_record: 10, page: 1},
};

export const userPreferenceSlice = createSlice({
    name: "userPreference",
    initialState,
    reducers: {
        setDrawerAperto: (state, action: PayloadAction<boolean>) => {
            state.preferenzeUtente.drawer_aperto = action.payload;
        },
        setUserStored: (state, action: PayloadAction<UserStored>) => {
            state.userStored = action.payload;
        },

        tokenRefresh: (
            state,
            action: PayloadAction<{
                access?: string;
                refresh?: string;
            }>
        ) => {
            if (state.userStored) {
                state.userStored = {
                    ...state.userStored,
                    ...action.payload,
                    // ✅ NUOVO: aggiorna scadenza refresh quando fai refresh
                    refreshExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 giorni
                };
            }
        },

        resetUserStored: (state) => {
            state.userStored = undefined;
        },

        updateRememberedUsername: (state, action: PayloadAction<{username: string, remember: boolean}>) => {
            if (state.userStored) {
                state.userStored.username = action.payload.username;
                state.userStored.rememberUsername = action.payload.remember;
            } else if (action.payload.remember) {
                state.userStored = {
                    username: action.payload.username,
                    rememberUsername: true
                };
            }
        },


        setFiltriOdPSeguiti: (state, action: PayloadAction<FiltriOdPLegacy>) => {
            state.filtriOdPSeguiti = action.payload;
        },
        resetFiltriOdPSeguiti: (state) => {
            state.filtriOdPSeguiti = undefined;
        },
        setFiltriOdPSpallaSchedulazione: (state, action: PayloadAction<FiltriOdPLegacy>) => {
            state.filtriOdPSpallaSchedulazione = action.payload;
        },
        resetFiltriOdPSpallaSchedulazione: (state) => {
            state.filtriOdPSpallaSchedulazione = undefined;
        },
        setFiltriVisualizzazioneGantt: (state, action: PayloadAction<FiltriVisualizzazioneGantt>) => {
            state.filtriVisualizzazioneGantt = action.payload;
        },
        resetFiltriVisualizzazioneGantt: (state) => {
            state.filtriVisualizzazioneGantt = {dataDa: convertToDateTime(new Date())?.setLocale("it").toFormat("yyyy-MM-dd"), numeroSettimane: 4};
        },
        setFiltriOstacoli: (state, action: PayloadAction<FiltriOstacoli>) => {
            state.filtriOstacoli = action.payload;
        },
        resetFiltriOstacoli: (state) => {
            state.filtriOstacoli = undefined;
        },
        setFiltriFasiFrazioniReparto: (state, action: PayloadAction<FiltriFasiFrazioniReparto>) => {
            state.filtriFasiFrazioniReparto = action.payload;
        },
        resetFiltriFasiFrazioniReparto: (state) => {
            state.filtriFasiFrazioniReparto = undefined;
        },
    },
    extraReducers: (builder) => {
    },
});

// Action creators are generated for each case reducer function
export const {
    setDrawerAperto,
    // setPreferenzeListaTasks,
    setUserStored,
    tokenRefresh,
    resetUserStored,
    updateRememberedUsername,
    setFiltriOdPSeguiti,
    resetFiltriOdPSeguiti,
    setFiltriOdPSpallaSchedulazione,
    resetFiltriOdPSpallaSchedulazione,
    setFiltriVisualizzazioneGantt,
    resetFiltriVisualizzazioneGantt,
    setFiltriOstacoli,
    resetFiltriOstacoli,
    setFiltriFasiFrazioniReparto,
    resetFiltriFasiFrazioniReparto,
} = userPreferenceSlice.actions;

listenerMiddleware.startListening({
    actionCreator: authenticationSlice.actions.resetUtente,
    effect: (action, listenerApi) => {
        listenerApi.dispatch(userPreferenceSlice.actions.resetUserStored());
    },
});

export const userPreferenceReducer = userPreferenceSlice.reducer;
